import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getPlan } from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import { CardContent } from '@/common/ui/card';
import { FileViewModal } from '@/areas/settings/repository-management/components/FileViewModal';
import { FileTypes, getFileTypeIcon } from 'common/constants/filetypes';
import { Icon } from 'semantic-ui-react';
import { isYouTubeWatchUrl, openResourceLink } from 'common/utils/resourceLinks';
import { Lightbulb, BookOpen, Bookmark } from 'lucide-react';
import { PageSection, BodyText, PageHeader, H2, H4, PageContent } from '@/common/styles/page';
import CultureActions from '../culture-actions/CultureActions';
import { AppConfig } from '../../../../../Config';
import { authenticatedFetch } from '../../../../../common/utils/authFetch';
import { getCurrentOrganizationId } from '../../../../../common/selectors/auth';

const firstString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const pickString = (source: any, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = firstString(source?.[key]);
    if (value) {
      return value;
    }
  }
  return undefined;
};

const parseType = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    if (lowered === 'url' || lowered === 'link') return FileTypes.URL;
    if (lowered === 'pdf') return FileTypes.PDF;
    if (lowered === 'mp4' || lowered === 'video') return FileTypes.MP4;
    const parsed = Number(lowered);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const normalizeResourceItem = (resource: any, index: number) => {
  if (typeof resource === 'string') {
    const text = resource.trim();
    if (!text) return null;
    const isUrl = /^https?:\/\//i.test(text);
    return {
      id: undefined as number | undefined,
      name: text,
      title: text,
      object_key: isUrl ? text : '',
      key: isUrl ? text : '',
      type: isUrl ? FileTypes.URL : undefined,
      __normalized_key: `string-${index}-${text}`,
    };
  }

  if (!resource || typeof resource !== 'object') {
    return null;
  }

  const link =
    firstString(resource.object_key) ||
    firstString(resource.objectKey) ||
    firstString(resource.url) ||
    firstString(resource.link) ||
    firstString(resource.href) ||
    firstString(resource.key);

  const name =
    pickString(resource, ['name', 'title', 'text', 'label', 'filename', 'file_name']) ||
    link ||
    'Resource';

  const parsedId =
    typeof resource.id === 'number'
      ? resource.id
      : typeof resource.repository_file_id === 'number'
        ? resource.repository_file_id
        : typeof resource.file_id === 'number'
          ? resource.file_id
          : undefined;

  const type = parseType(resource.type) ?? (link ? FileTypes.URL : undefined);

  return {
    ...resource,
    id: parsedId,
    name,
    title: resource.title || name,
    object_key: link || '',
    key: resource.key || link || '',
    type,
    __normalized_key: `${parsedId ?? 'no-id'}-${index}-${name}`,
  };
};

const normalizeResources = (item: any): any[] => {
  const contentItems = Array.isArray(item?.content) ? item.content : [];
  const resourceItems = Array.isArray(item?.resources) ? item.resources : [];

  const normalized = [...contentItems, ...resourceItems]
    .map((entry, index) => normalizeResourceItem(entry, index))
    .filter(Boolean) as any[];

  return normalized;
};

const isYouTubeResource = (resource: any): boolean => {
  const url = firstString(resource?.object_key) || firstString(resource?.key);
  return Boolean(url && isYouTubeWatchUrl(url));
};


const FocusAreaDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const plan = useSelector(getPlan);

  // Try to read item from navigation state first
  const stateItem = (location.state && (location.state as any).item) as any | undefined;
  let item = stateItem;

  // Viewer modal state (declare hooks unconditionally to satisfy Rules of Hooks)
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [selectedContent, setSelectedContent] = React.useState<{ id: number; name?: string } | null>(null);
  const organizationId = useSelector(getCurrentOrganizationId);

  const recordLinkOpen = async (url: string, resourceName?: string, repositoryFileId?: number) => {
    if (!organizationId) {
      return;
    }

    const apiUrl = AppConfig.api.apiUrl || '';
    const endpoint = `${apiUrl}/api/portal/v1/assessments/leaderboard-events/link-open/`;

    try {
      await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-NULOGIC-ORGANIZATION-ID': String(organizationId),
        },
        body: JSON.stringify({
          url,
          resource_name: resourceName,
          repository_file_id: repositoryFileId,
        }),
      });
    } catch (error) {
      console.warn('Failed to record link open leaderboard event', error);
    }
  };

  if (!item && params.templateId && typeof params.taskIndex !== 'undefined') {
    const idx = Number(params.taskIndex);
    item = plan.find((p: any) => String(p.recommendation_template_id) === String(params.templateId) && Number(p.task_index) === idx);
  }

  if (!item) {
    return (
      <PageContent>
        <PageSection>
          <PageHeader
            title="Recommendation not found"
            caption="We couldn't find the recommendation details for this item."
            onBack={() => navigate(-1)}
            backText="Back to Focus Plan"
          />

        </PageSection>
      </PageContent>
    );
  }

  const referencesText = pickString(item, ['references']);
  const supportingScienceText = pickString(item, ['supportingScience', 'supporting_science']);
  const descriptionText = pickString(item, ['description']) || 'No description available';
  const practicalExampleText = pickString(item, ['practicalExample', 'practical_example']);
  const softSkillsText = pickString(item, ['softSkills', 'soft_skills']);
  const areaOfFocusText = pickString(item, ['areaOfFocus', 'area_of_focus']);
  const implementationTimeText = pickString(item, ['processChangeImplementationTime', 'process_change_implementation_time']);
  const howToImplementText = pickString(item, ['howToImplement', 'how_to_implement']);
  const whatNotToDoText = pickString(item, ['whatNotToDo', 'what_not_to_do']);
  const howToKnowItsWorkingText = pickString(item, ['howToKnowItsWorking', 'how_to_know_its_working']);
  const suggestedProcessChangesText = pickString(item, ['suggestedProcessChanges', 'suggested_process_changes', 'processChanges', 'process_changes']);
  const resources = normalizeResources(item);

  return (
    <PageSection padtop="0">
      <CardContent className="p-6 space-y-6" style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>

        {selectedContent && (
          <FileViewModal
            fileId={String(selectedContent.id)}
            isOpen={viewerOpen}
            setModalOpen={setViewerOpen}
            title={selectedContent.name || 'Resource'}
          />
        )}

        <PageSection>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: '1 1 auto' }}>
              <PageHeader
                title={item.task_title || item.title || item.recommendation_name}
                caption={referencesText ? `${supportingScienceText ? `${supportingScienceText} • ` : ''}${referencesText}` : undefined}
                onBack={() => navigate(-1)}
                backText="Back to Focus Plan"
              />
            </div>
          </div>

          <PageSection display="block" style={{ marginTop: '12px' }}>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-1 md:divide-x md:divide-gray-300">

                {/* Overview column */}
                <div className="space-y-4 md:pr-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full shadow-sm" style={{ background: '#f59e0b' }}>
                      <Lightbulb className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <H2>Overview</H2>
                    </div>
                  </div>

                  <div className="pl-6">
                    <H4>Description</H4>
                    <BodyText className="text-sm text-gray-700">{descriptionText}</BodyText>
                  </div>

                  {practicalExampleText && (
                    <div className="pl-6">
                      <H4>Practical example</H4>
                      <BodyText className="text-sm text-gray-700">“{practicalExampleText}”</BodyText>
                    </div>
                  )}

                  {softSkillsText && (
                    <div className="pl-6">
                      <H4>Soft skills</H4>
                      <BodyText className="text-sm text-gray-700">{softSkillsText}</BodyText>
                    </div>
                  )}
                  {areaOfFocusText && (
                    <div className="pl-6">
                      <H4>Area of focus</H4>
                      <BodyText className="text-sm text-gray-700">{areaOfFocusText}</BodyText>
                    </div>
                  )}

                  {implementationTimeText && (
                    <div className="pl-6">
                      <H4>Implementation time</H4>
                      <BodyText className="text-sm text-gray-700">{implementationTimeText}</BodyText>
                    </div>
                  )}
                </div>

                {/* Learn More column */}
                <div className="space-y-4 md:px-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full shadow-sm" style={{ background: '#7c3aed' }}>
                      <BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <H2>Learn More</H2>
                    </div>
                  </div>

                  <div className="space-y-3">

                    {howToImplementText && (
                      <div className="pl-6">
                        <H4>How to implement</H4>
                        <BodyText className="text-sm text-gray-700">{howToImplementText}</BodyText>
                      </div>
                    )}

                    {whatNotToDoText && (
                      <div className="pl-6">
                        <H4>What not to do</H4>
                        <BodyText className="text-sm text-gray-700">{whatNotToDoText}</BodyText>
                      </div>
                    )}

                    {howToKnowItsWorkingText && (
                      <div className="pl-6">
                        <H4>How to know it's working</H4>
                        <BodyText className="text-sm text-gray-700">{howToKnowItsWorkingText}</BodyText>
                      </div>
                    )}

                    {suggestedProcessChangesText && (
                      <div className="pl-6">
                        <H4>Suggested process changes</H4>
                        <BodyText className="text-sm text-gray-700">{suggestedProcessChangesText}</BodyText>
                      </div>
                    )}

                  </div>
                </div>

                {/* Resources column */}
                <div className="space-y-4 md:pl-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full shadow-sm" style={{ background: '#10b981' }}>
                      <Bookmark className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <H2>Resources</H2>
                    </div>
                  </div>

                  {resources.length > 0 ? (
                    <div className="mt-3 rounded-lg overflow-hidden bg-white">
                      {resources.map((c: any, i: number) => {
                        const handleRowAction = (e?: React.SyntheticEvent) => {
                          if (e) e.stopPropagation();

                          openResourceLink(c, {
                            recordLinkOpen: (url, resourceName, repositoryFileId) => {
                              void recordLinkOpen(url, resourceName, repositoryFileId);
                            },
                            openInApp: () => {
                              if (c.id) {
                                setSelectedContent({ id: c.id, name: c.name });
                                setViewerOpen(true);
                              }
                            },
                            openInNewTab: url => window.open(url, '_blank', 'noopener'),
                          });
                        };

                        return (
                          <div
                            key={c.__normalized_key || i}
                            role="button"
                            tabIndex={0}
                            onClick={(e) => handleRowAction(e)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRowAction(e);
                              }
                            }}
                            aria-label={`Open ${c.name || c.title || 'resource'}`}
                            className="flex items-center p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                          >
                            <div className="w-4 h-4 self-start mt-1 flex items-center justify-center rounded-md bg-white mr-3 shadow-none border-0">
                              {isYouTubeResource(c)
                                ? getFileTypeIcon(FileTypes.MP4)
                                : (typeof c.type === 'number') ? getFileTypeIcon(c.type) : <Icon name="file outline" />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <H4>{c.name || c.title || '—'}</H4>
                              <div className="text-sm text-gray-500">
                                {isYouTubeResource(c) ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <Icon name="youtube" color="red" style={{ margin: 0 }} />
                                    <span>YouTube</span>
                                  </span>
                                ) : (
                                  c.object_key || c.key || ''
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <BodyText className="text-sm text-gray-500">No resources available</BodyText>
                  )}
                </div>

              </div>
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-md p-6">
              <CultureActions actionPlanId={item?.action_plan_id ?? null} />
            </div>
          </PageSection>

        </PageSection>

      </CardContent>
    </PageSection>
  );
};

export default FocusAreaDetail;
